import { useState } from 'react';

import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { conversationsUtilsEndpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { isUuid } from '@/util/util';

export type ConversationDownload = {
  canDownload: boolean;
  isDownloading: boolean;
  start: () => void;
};

export const useDownloadConversation = (
  sessionId: string,
  onDownloaded: () => void
): ConversationDownload => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const start = async () => {
    setIsDownloading(true);
    trackEvent(EventName.ClickAnalyticsLogsExport, { session_id: sessionId });

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const blob = await callGet(conversationsUtilsEndpoints.downloadHistory(sessionId, timezone), {
        responseType: 'blob',
      });
      saveAs(blob, `conversation_${sessionId}.pdf`);
      onDownloaded();
    } catch (err) {
      console.error('Failed to download PDF', err);
      toast.error(t('try_it.download_as_pdf_error_toast'));
    } finally {
      setIsDownloading(false);
    }
  };

  return { canDownload: isUuid(sessionId), isDownloading, start };
};
